/**
 * One store for everything the shell and more than one screen need to agree about.
 *
 * Navigation lives here rather than in a navigator because the master–detail panels (a signal and
 * its plan, a Sync round) render as a pushed screen when narrow and as a second pane when wide,
 * off the same selection. A selection owned by a navigator cannot do that.
 *
 * Screens own their own data; the store owns identity, the badge counts, pending invites, and the
 * two pieces of chrome state (theme, connection).
 */
import { create } from 'zustand';
import { Platform } from 'react-native';
import { api, ApiError } from './api';
import * as storage from './storage';
import type { ScreenId } from './registry';

/**
 * Which of the two designs the app is wearing.
 *
 * Restyle round v1 landed TWO complete looks rather than one — Bouquet Final, the default, and
 * Tuxedo Final beside it — each with full light and dark modes. A look is not a mode: `theme`
 * says whether the lights are on, and `look` says which room you are in. The pair is chosen in
 * Settings and persisted, and every value either one decides lives in `global.css`, keyed off
 * `data-look` on the document root.
 */
export type Look = 'bouquet' | 'tuxedo';

const LOOK_KEY = 'hunsy.look';
const LOOKS: readonly Look[] = ['bouquet', 'tuxedo'];

function isLook(v: unknown): v is Look {
  return typeof v === 'string' && (LOOKS as readonly string[]).includes(v);
}

/**
 * Put the look on the document root, where the CSS can see it.
 *
 * Web only, and not because native has no second look — it has. A phone has no document to put an
 * attribute on and no stylesheet to match it, so native's half of the switch is a NativeWind
 * `vars()` table on the app root View in `App.tsx`, built from `src/lib/looks.ts`. Both halves
 * read `look` out of this store, and both read the same values; this function is just the web's
 * way of delivering them.
 */
function applyLook(look: Look): void {
  if (Platform.OS !== 'web') return;
  const root = globalThis.document?.documentElement;
  if (root) root.dataset.look = look;
}

// Before the first paint, not after it. The store is created at import time and this runs with it,
// so the very first frame is already drawn in the right room; the persisted read below cannot be
// synchronous (SecureStore is async on native) and would otherwise show one frame of bouquet.
// Native needs no equivalent call: bouquet is the bare tree with no vars over it.
applyLook('bouquet');

export interface Me {
  partner: 'A' | 'B';
  name: string;
  partnerName: string;
  /** Server-side, so the Signals walkthrough follows the partner rather than the device. */
  walkthroughSeen: boolean;
}

/**
 * Who did a thing, as the viewer should read it.
 *
 * `'app'` is first on purpose. Every local copy of this helper started life as a two-branch
 * "me, else them" ternary, which sends anything that is not the viewer to the partner's name —
 * so a row the surfacing engine planted gets captioned with the name of the one person it must
 * never be traceable to. That bug shipped in the List once. One helper, with the non-person case
 * handled before the partner case, is how it stays fixed everywhere.
 */
export function whoLabel(
  p: 'A' | 'B' | 'app',
  me: Me | null,
  labels: { self: string; app?: string } = { self: 'you' },
): string {
  if (p === 'app') return labels.app ?? 'the game';
  if (!me) return p;
  return p === me.partner ? labels.self : me.partnerName;
}

/** A signal the app has half-written for you — Sync's "make it a signal" fills this in. */
export interface SignalDraft {
  kind: 'request' | 'desire' | 'frustration';
  body: string;
}

/** One choosable identity, offered only while the server's debug picker is switched on. */
export interface Identity {
  partner: 'A' | 'B';
  name: string;
  key: string;
}

export interface Invite {
  id: string;
  module: string;
  refId: string | null;
  /**
   * Who rang the doorbell, to the only resolution the server is willing to give: a person, or the
   * game. Never which person — the sender's id is deliberately absent from the payload — but a
   * screen that assumes "person" will caption an app-sent invite with the partner's name.
   */
  from: 'partner' | 'app';
  createdAt: string;
}

export type SessionState = 'boot' | 'pick' | 'unpaired' | 'badkey' | 'ready';

interface HunsyStore {
  screen: ScreenId;
  go: (screen: ScreenId) => void;

  /**
   * The master–detail selection, per panel. It lives here rather than in a navigator because the
   * same selection has to render as a pushed view when narrow and as a second pane when wide —
   * one body of state, two arrangements. A navigator can only do the first.
   */
  selected: Partial<Record<ScreenId, string | null>>;
  select: (screen: ScreenId, id: string | null) => void;

  /**
   * The two full-page views that are not tabs: the preference sheet and the Signals walkthrough.
   *
   * They sit in the shell, over whatever screen is current, because neither is somewhere you
   * navigate TO — one is opened from two places, the other opens itself once — and giving either
   * a tab would advertise it forever for something you do twice.
   */
  overlay: 'prefs' | 'walkthrough' | null;
  openOverlay: (overlay: 'prefs' | 'walkthrough' | null) => void;

  /**
   * A signal composed somewhere else and handed to the Signals box. Consumed on arrival, so
   * coming back to Signals later does not re-fill a box the author already emptied.
   */
  signalDraft: SignalDraft | null;
  setSignalDraft: (draft: SignalDraft | null) => void;

  /**
   * WHAT IS IN THE BOX RIGHT NOW — the Signals compose field and the kind chosen beside it.
   *
   * Store state and not the screen's, because `App.tsx` renders one screen at a time: a tab away
   * and back unmounts `SignalsScreen`, and the half-written sentence somebody had been working up
   * to went with it. That is the worst possible thing for this particular box to do — it is the
   * one place in the app people are asked to say the hard thing, and losing it once teaches them
   * to write the safe version next time.
   *
   * NOT persisted to storage, deliberately. An unsent signal surviving a cold start is the
   * author's own words sitting on the device for a partner to find, which is the one artefact this
   * product exists to avoid. It lives as long as the app is open and no longer.
   */
  compose: SignalDraft;
  setCompose: (patch: Partial<SignalDraft>) => void;
  clearCompose: () => void;

  theme: 'light' | 'dark';
  toggleTheme: () => void;

  /** Which of the two designs is on. See `Look` — a look is a room, a theme is the lights. */
  look: Look;
  setLook: (look: Look) => void;

  online: boolean;
  setOnline: (online: boolean) => void;

  error: string | null;
  fail: (e: unknown) => void;
  dismissError: () => void;

  session: SessionState;
  setSession: (state: SessionState) => void;
  me: Me | null;
  /** True right after a fresh ?key= pairing — Home uses it to ask for push in context. */
  justPaired: boolean;
  setJustPaired: (value: boolean) => void;
  /** Populated only when the server's debug picker is on; empty means normal key pairing. */
  identities: Identity[];
  setIdentities: (identities: Identity[]) => void;

  badges: Record<string, number>;
  refreshBadges: () => Promise<void>;

  invites: Invite[];
  refreshInvites: () => Promise<void>;
  invitesFor: (module: string) => Invite[];
  acceptInvite: (invite: Invite) => Promise<void>;
  dismissInvite: (invite: Invite) => Promise<void>;

  refreshMe: () => Promise<void>;
  rename: (name: string) => Promise<void>;
  markWalkthroughSeen: () => Promise<void>;
}

export const useStore = create<HunsyStore>((set, get) => ({
  screen: 'home',
  go: (screen) => set({ screen, overlay: null }),

  selected: {},
  select: (screen, id) => set((s) => ({ selected: { ...s.selected, [screen]: id } })),

  overlay: null,
  openOverlay: (overlay) => set({ overlay }),

  signalDraft: null,
  setSignalDraft: (signalDraft) => set({ signalDraft }),

  compose: { kind: 'request', body: '' },
  setCompose: (patch) => set((s) => ({ compose: { ...s.compose, ...patch } })),
  // The kind goes back to the default with the words: a box that keeps "Frustration" selected
  // after a signal is filed is pre-answering the next one.
  clearCompose: () => set({ compose: { kind: 'request', body: '' } }),

  theme: 'light',
  toggleTheme: () => set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),

  look: 'bouquet',
  setLook: (look) => {
    set({ look });
    applyLook(look);
    // Best-effort, like every other write to this store: the look is already on screen, and a
    // storage failure costs the choice at the next cold start rather than the choice itself.
    void storage.setItem(LOOK_KEY, look);
  },

  online: true,
  setOnline: (online) => set({ online }),

  error: null,
  fail: (e) => {
    // Offline is a banner, not a dialog: the shell already says "reconnecting" and the action can
    // simply be tried again.
    if (e instanceof ApiError && e.status === 0) return;
    set({ error: e instanceof Error ? e.message : String(e) });
  },
  dismissError: () => set({ error: null }),

  session: 'boot',
  setSession: (session) => set({ session }),
  me: null,
  justPaired: false,
  setJustPaired: (justPaired) => set({ justPaired }),
  identities: [],
  setIdentities: (identities) => set({ identities }),

  badges: {},
  refreshBadges: async () => {
    try {
      set({ badges: await api.get<Record<string, number>>('/api/badges') });
    } catch {
      // Offline — keep the last known counts.
    }
  },

  invites: [],
  refreshInvites: async () => {
    try {
      set({ invites: await api.get<Invite[]>('/api/invites') });
    } catch {
      /* offline */
    }
  },
  invitesFor: (module) => get().invites.filter((i) => i.module === module),
  acceptInvite: async (invite) => {
    await api.post(`/api/invites/${invite.id}/accept`);
    await Promise.all([get().refreshInvites(), get().refreshBadges()]);
  },
  dismissInvite: async (invite) => {
    // Silent by design: the sender never learns either way. Receipts create pressure.
    await api.post(`/api/invites/${invite.id}/dismiss`);
    await Promise.all([get().refreshInvites(), get().refreshBadges()]);
  },

  /** Every /api/me is also the last_seen_at heartbeat the nudge engine reads. */
  refreshMe: async () => {
    try {
      set({ me: await api.get<Me>('/api/me'), session: 'ready' });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) set({ session: 'badkey' });
      else if (get().session === 'boot') {
        // Offline on first load: show the shell anyway if we ever paired before.
        set({ session: api.configured ? 'ready' : 'unpaired' });
      }
    }
  },

  rename: async (name) => {
    await api.patch('/api/me', { name });
    await get().refreshMe();
  },

  /** Stamped once, server-side. Skipping and finishing are the same promise: do not ask again. */
  markWalkthroughSeen: async () => {
    set({ me: get().me ? { ...get().me!, walkthroughSeen: true } : null });
    await api.patch('/api/me', { walkthroughSeen: true }).catch(() => undefined);
    await get().refreshMe();
  },
}));

/**
 * The persisted look, restored as soon as storage can answer.
 *
 * It cannot be part of the store's initial state: `storage.getItem` is async on both platforms
 * (SecureStore on native has no synchronous read), so the store is created in bouquet and this
 * corrects it a tick later — which is why `applyLook` runs at module load as well, so the correct
 * DEFAULT is never a frame late even though a saved override may be.
 *
 * It goes through `setState` rather than `setLook` on purpose: this is reading what was already
 * saved, and writing it straight back would be a storage round trip that can only ever store the
 * value it just read.
 */
void storage
  .getItem(LOOK_KEY)
  .then((saved) => {
    if (!isLook(saved) || saved === useStore.getState().look) return;
    useStore.setState({ look: saved });
    applyLook(saved);
  })
  .catch(() => undefined);
